<template>
  <v-card class="product-list-panel" height="calc(100vh - 120px)">
    <v-card-title class="py-3">
      <v-icon left>mdi-package-variant</v-icon>
      Lista Prodotti
      <v-spacer></v-spacer>
      <v-chip small color="primary" outlined>
        {{ totalProducts }}
      </v-chip>
    </v-card-title>

    <!-- Filters Section -->
    <v-card-text class="pb-0">
      <!-- Family Filters -->
      <v-row dense>
        <v-col cols="12">
          <v-autocomplete
            v-model="selectedFamily"
            :items="families"
            item-text="onlyname"
            item-value="ACL_CODCLA1"
            label="Famiglia"
            dense
            clearable
            @change="onFamilyChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.onlyname }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12">
          <v-autocomplete
            v-model="selectedSubFamily"
            :items="subFamilies"
            item-text="onlyname"
            item-value="ACL_CODCLA2"
            label="Sottofamiglia"
            dense
            clearable
            :disabled="!selectedFamily"
            @change="applyFilters"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.onlyname }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <!-- Search Field -->
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="searchQuery"
            label="Cerca prodotto"
            prepend-inner-icon="mdi-magnify"
            dense
            clearable
            @input="onSearch"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Products List with Virtual Scrolling -->
    <div class="products-container">
      <div 
        ref="scrollContainer"
        class="scroll-container"
        @scroll="onScroll"
      >
        <div :style="{ height: `${totalHeight}px` }" class="scroll-spacer">
          <div 
            :style="{ transform: `translateY(${offsetY}px)` }"
            class="visible-items"
          >
            <div
              v-for="product in visibleProducts"
              :key="product.id"
              class="product-item"
              draggable="true"
              @dragstart="onDragStart($event, product)"
              @dragend="onDragEnd"
            >
              <v-card 
                class="mb-2 product-card" 
                :class="{ 'dragging': isDragging && draggedProduct?.id === product.id }"
                elevation="2"
              >
                <!-- Product Image -->
                <div class="product-image-container">
                  <ProductImage 
                    :productId="product.cod_articolo"
                    :height="80"
                    class="product-image"
                  />
                  <div class="drag-handle">
                    <v-icon small>mdi-drag</v-icon>
                  </div>
                </div>

                <!-- Product Info -->
                <v-card-text class="pa-2">
                  <div class="product-title">
                    {{ product.description }}
                  </div>
                  <div class="product-code text-caption text--secondary">
                    COD: {{ product.cod_articolo }}
                  </div>
                  
                  <!-- Dimensions -->
                  <div class="dimensions mt-1">
                    <v-chip x-small color="blue-grey" outlined class="mr-1">
                      A: {{ product.altezza || '?' }}cm
                    </v-chip>
                    <v-chip x-small color="blue-grey" outlined class="mr-1">
                      L: {{ product.larghezza || '?' }}cm
                    </v-chip>
                    <v-chip x-small color="blue-grey" outlined>
                      P: {{ product.profondita || '?' }}cm
                    </v-chip>
                  </div>

                  <!-- Stock Info -->
                  <div class="stock-info mt-1">
                    <v-chip 
                      x-small 
                      :color="product.quantita_sede > 0 ? 'green' : 'red'"
                      :text-color="product.quantita_sede > 0 ? 'white' : 'white'"
                    >
                      Stock: {{ product.quantita_sede || 0 }}
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="text-center pa-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-2 text-caption">Caricamento prodotti...</div>
      </div>

      <!-- No Results -->
      <div v-if="!loading && filteredProducts.length === 0" class="text-center pa-4">
        <v-icon large color="grey">mdi-package-variant-closed</v-icon>
        <div class="mt-2 text-body-2 grey--text">Nessun prodotto trovato</div>
      </div>
    </div>
  </v-card>
</template>

<script>
import ProductImage from '../ProductImage.vue'
import requests from '../../requests'

const ITEM_HEIGHT = 180 // Approximate height of each product item
const BUFFER_SIZE = 5 // Number of items to render outside visible area

export default {
  name: 'PlanogramProductList',
  components: {
    ProductImage
  },
  data() {
    return {
      // Products data
      allProducts: [],
      filteredProducts: [],
      loading: false,

      // Filtering
      families: [],
      subFamilies: [],
      selectedFamily: null,
      selectedSubFamily: null,
      searchQuery: '',

      // Virtual scrolling
      scrollTop: 0,
      containerHeight: 0,
      visibleStartIndex: 0,
      visibleEndIndex: 0,

      // Drag and drop
      isDragging: false,
      draggedProduct: null,

      // Search debounce
      searchTimeout: null
    }
  },
  computed: {
    totalProducts() {
      return this.filteredProducts.length
    },

    totalHeight() {
      return this.filteredProducts.length * ITEM_HEIGHT
    },

    offsetY() {
      return this.visibleStartIndex * ITEM_HEIGHT
    },

    visibleProducts() {
      return this.filteredProducts.slice(this.visibleStartIndex, this.visibleEndIndex)
    }
  },
  async mounted() {
    await this.loadFamilies()
    await this.loadProducts()
    this.setupVirtualScrolling()
  },
  methods: {
    // Data loading
    async loadProducts() {
      this.loading = true
      try {
        // Use specific planogram products function
        const response = await requests.getPlanogramProducts()
        
        this.allProducts = response.data.map(product => ({
          id: product.ar_codart,
          cod_articolo: product.ar_codart,
          description: product.ar_descr,
          altezza: product.ar_hhmisura1,
          larghezza: product.ar_hhmisura2,
          profondita: product.ar_hhmisura3,
          quantita_sede: product.xx_dissede,
          family: product.acl_descla1,
          sub_family: product.acl_descla2,
          barcode: product.bc_code,
          prezzo_vendita: product.lc_prezzo2
        }))
        
        this.filteredProducts = [...this.allProducts]
        this.updateVisibleRange()
      } catch (error) {
        console.error('Error loading products:', error)
        this.allProducts = []
        this.filteredProducts = []
      } finally {
        this.loading = false
      }
    },

    async loadFamilies() {
      try {
        const response = await requests.getFamilies()
        this.families = response.data
      } catch (error) {
        console.error('Error loading families:', error)
        this.families = []
      }
    },

    async loadSubFamilies(familyId) {
      if (!familyId) {
        this.subFamilies = []
        return
      }

      try {
        const response = await requests.getSubFamilies(familyId)
        this.subFamilies = response.data
      } catch (error) {
        console.error('Error loading subfamilies:', error)
        this.subFamilies = []
      }
    },

    // Filtering methods
    onFamilyChange() {
      this.selectedSubFamily = null
      if (this.selectedFamily) {
        this.loadSubFamilies(this.selectedFamily)
      } else {
        this.subFamilies = []
      }
      this.applyFilters()
    },

    onSearch() {
      // Debounce search to avoid too many filter operations
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 300)
    },

    applyFilters() {
      let filtered = [...this.allProducts]

      // Family filter
      if (this.selectedFamily) {
        const familyData = this.families.find(f => f.ACL_CODCLA1 === this.selectedFamily)
        if (familyData) {
          filtered = filtered.filter(p => p.family === familyData.ACL_DESCLA1)
        }
      }

      // Subfamily filter
      if (this.selectedSubFamily) {
        const subFamilyData = this.subFamilies.find(sf => sf.ACL_CODCLA2 === this.selectedSubFamily)
        if (subFamilyData) {
          filtered = filtered.filter(p => p.sub_family === subFamilyData.ACL_DESCLA2)
        }
      }

      // Search filter
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const query = this.searchQuery.toLowerCase().trim()
        filtered = filtered.filter(p => 
          p.description.toLowerCase().includes(query) ||
          p.cod_articolo.toLowerCase().includes(query)
        )
      }

      this.filteredProducts = filtered
      this.updateVisibleRange()
    },

    // Virtual scrolling
    setupVirtualScrolling() {
      this.$nextTick(() => {
        const container = this.$refs.scrollContainer
        if (container) {
          this.containerHeight = container.clientHeight
          this.updateVisibleRange()
        }
      })
    },

    onScroll(event) {
      this.scrollTop = event.target.scrollTop
      this.updateVisibleRange()
    },

    updateVisibleRange() {
      const visibleCount = Math.ceil(this.containerHeight / ITEM_HEIGHT)
      const startIndex = Math.floor(this.scrollTop / ITEM_HEIGHT)
      
      this.visibleStartIndex = Math.max(0, startIndex - BUFFER_SIZE)
      this.visibleEndIndex = Math.min(
        this.filteredProducts.length,
        startIndex + visibleCount + BUFFER_SIZE
      )
    },

    // Drag and drop
    onDragStart(event, product) {
      this.isDragging = true
      this.draggedProduct = product
      
      // Create drag image
      const dragImage = document.createElement('div')
      dragImage.innerHTML = `
        <div style="
          background: white;
          border: 2px solid #1976d2;
          border-radius: 8px;
          padding: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          font-size: 12px;
          max-width: 150px;
        ">
          <div style="font-weight: bold; margin-bottom: 4px;">${product.description}</div>
          <div style="color: #666;">L:${product.larghezza || '?'} P:${product.profondita || '?'} A:${product.altezza || '?'}</div>
        </div>
      `
      dragImage.style.position = 'absolute'
      dragImage.style.top = '-1000px'
      document.body.appendChild(dragImage)
      
      event.dataTransfer.setDragImage(dragImage, 75, 40)
      event.dataTransfer.setData('text/plain', JSON.stringify(product))
      
      setTimeout(() => document.body.removeChild(dragImage), 0)
      
      this.$emit('start-drag', product)
    },

    onDragEnd() {
      this.isDragging = false
      this.draggedProduct = null
      this.$emit('end-drag')
    }
  }
}
</script>

<style scoped>
.product-list-panel {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.products-container {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.scroll-container {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.scroll-spacer {
  position: relative;
}

.visible-items {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.product-item {
  cursor: grab;
  user-select: none;
}

.product-item:active {
  cursor: grabbing;
}

.product-card {
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.product-card:hover {
  border-color: #1976d2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
}

.product-card.dragging {
  opacity: 0.5;
  transform: rotate(5deg);
}

.product-image-container {
  position: relative;
  height: 80px;
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.drag-handle {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(25, 118, 210, 0.9);
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.product-card:hover .drag-handle {
  opacity: 1;
}

.product-title {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  max-height: 2.4em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-code {
  font-size: 10px;
  margin-top: 2px;
}

.dimensions {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.dimensions .v-chip {
  font-size: 9px !important;
  height: 16px !important;
}

.stock-info {
  display: flex;
  justify-content: flex-end;
}

.stock-info .v-chip {
  font-size: 9px !important;
  height: 16px !important;
}

/* Scrollbar styling */
.scroll-container::-webkit-scrollbar {
  width: 6px;
}

.scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>